import { Link } from "react-router-dom";
import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface PageHeaderAction {
  label: string;
  icon?: LucideIcon;
  onClick?: () => void;
  href?: string;
  variant?: "default" | "primary";
}

export interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumb?: BreadcrumbItem[];
  actions?: PageHeaderAction[];
  // Escape hatch for action UI the declarative `actions` API can't express
  // (e.g. a permission-gated button, a control with a loading/disabled state,
  // or a custom menu). Rendered in the same action row, after `actions`.
  actionsSlot?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  breadcrumb,
  actions,
  actionsSlot,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn("flex flex-col", className)}>
      {breadcrumb && breadcrumb.length > 0 && (
        <div className="flex items-center gap-2 px-6 pt-4 pb-1 text-xs text-muted-foreground">
          {breadcrumb.map((item, index) => (
            <span key={item.label} className="flex items-center gap-2">
              {index > 0 && <span className="text-border select-none">/</span>}
              {index === breadcrumb.length - 1 ? (
                <span className="text-foreground font-medium">
                  {item.label}
                </span>
              ) : item.href ? (
                <Link
                  to={item.href}
                  className="hover:text-foreground transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <span>{item.label}</span>
              )}
            </span>
          ))}
        </div>
      )}

      <div
        className={cn(
          "flex items-end justify-between gap-4 px-6 pb-4 border-b border-border",
          breadcrumb && breadcrumb.length > 0 ? "pt-1" : "pt-4"
        )}
      >
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold tracking-[-0.01em] leading-tight m-0">
            {title}
          </h1>
          {description && (
            <p className="text-muted-foreground text-[13px] leading-[1.55] mt-[6px] max-w-[64ch]">
              {description}
            </p>
          )}
        </div>

        {((actions && actions.length > 0) || actionsSlot) && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {actions?.map((action, index) => {
              const Icon = action.icon;
              const base =
                "inline-flex items-center gap-1.5 h-[30px] px-3 rounded-md border text-xs font-medium transition-colors whitespace-nowrap cursor-pointer no-underline";
              const variant =
                action.variant === "primary"
                  ? "bg-foreground text-background border-foreground hover:bg-foreground/90"
                  : "bg-background text-foreground border-border hover:bg-accent";

              return action.href ? (
                <Link
                  key={`${index}-${action.label}`}
                  to={action.href}
                  className={cn(base, variant)}
                >
                  {Icon && <Icon className="size-3.5" />}
                  {action.label}
                </Link>
              ) : (
                <button
                  key={`${index}-${action.label}`}
                  type="button"
                  onClick={action.onClick}
                  className={cn(base, variant)}
                >
                  {Icon && <Icon className="size-3.5" />}
                  {action.label}
                </button>
              );
            })}
            {actionsSlot}
          </div>
        )}
      </div>
    </div>
  );
}
